// Keep the browser copy byte-identical; mediaPhotoEligibility.test.js checks parity.
const text = value => typeof value === 'string' && value.trim().length > 0;
const unknown = () => ({ eligible: false, status: 'unverifiable', reason: 'Photo criteria could not be verified. Analyze this photo again before submitting.' });

// Read-time validation must not normalize a user's criterion into a stronger
// or weaker catalog rule. Classify legacy flat items, but preserve their text.
function resolvePhotoValidationContract(data = {}) {
  const views = [];
  const constraints = [];
  for (const item of Array.isArray(data.requirements) ? data.requirements : []) {
    if (!text(item)) continue;
    const explicitView = /\b(front|back|rear|side|profile|pose|poses|view|framing|head\s+to\s+toe|feet\s+visible|entire\s+body)\b/i.test(item);
    const subject = /\b(front|back|rear|side|profile|double\s+bicep|posterior|chest|shoulder|arm|torso|leg|legs|quad|hamstring|calf|calves|lower\s+body|full[-\s]body|head\s+to\s+toe|feet\s+visible|entire\s+body|pose|poses|view)\b/i.test(item);
    const grooming = /\b(groom(?:ing|ed)?|hairless|body\s*hair|shorn)\b/i.test(item);
    const regionGrooming = grooming && /\b(legs?|underarms?|lower\s+body|thighs?|calf|calves)\b/i.test(item);
    const lighting = /\b(consistent|same|matching|even)\s+(lighting|exposure)\b|\blighting\s+and\s+exposure\b/i.test(item);
    if ((!explicitView && regionGrooming) || (!subject && (grooming || lighting))) constraints.push(item);
    else views.push(item);
  }
  constraints.push(...(Array.isArray(data.global_constraints) ? data.global_constraints : []));
  return { views, globalConstraints: [...new Set(constraints.filter(text))] };
}

function getPhotoEligibility(result, constraints = [], fileId = null) {
  if (!result || typeof result !== 'object') return unknown();
  const validation = result.photo_validation;
  if (validation?.version !== 1) {
    if (validation?.version !== undefined) return unknown();
    // Legacy unconstrained uploads retain their existing pose evidence, but an
    // explicit failed/malformed validation is never a pass. Constrained legacy
    // evidence requires reanalysis, not a database rewrite or an inferred pass.
    if (constraints.length > 0) return unknown();
    if (validation === undefined) return { eligible: true, status: 'pass', reason: null };
    if (validation?.meets_requirements === true && Array.isArray(validation.failed_checks) && validation.failed_checks.length === 0) {
      return { eligible: true, status: 'pass', reason: null };
    }
    return validation?.meets_requirements === false
      ? { eligible: false, status: 'fail', reason: 'Photo validation failed. Analyze or retake this photo before submitting.' }
      : unknown();
  }
  if (fileId !== null && validation.file_id !== String(fileId)) return unknown();
  if (typeof validation.meets_requirements !== 'boolean'
    || !Array.isArray(validation.failed_checks) || !validation.failed_checks.every(text)
    || !Array.isArray(validation.global_constraints)
    || validation.global_constraints.length !== constraints.length) return unknown();
  const checks = validation.global_constraints;
  if (!checks.every((check, i) => check && check.constraint === constraints[i]
    && ['pass', 'fail', 'unverifiable'].includes(check.status) && text(check.reason))) return unknown();
  const failed = checks.find(check => check.status === 'fail');
  const uncertain = checks.find(check => check.status === 'unverifiable');
  if (failed) return { eligible: false, status: 'fail', reason: failed.reason };
  if (uncertain) return { eligible: false, status: 'unverifiable', reason: uncertain.reason };
  if (!validation.meets_requirements || validation.failed_checks.length > 0) {
    return { eligible: false, status: 'fail', reason: validation.failed_checks[0] || 'This photo does not meet the configured photo criteria.' };
  }
  return { eligible: true, status: 'pass', reason: null };
}

function getFilePhotoEvidence(file, constraints = [], activityStatus = null) {
  const result = file?.inferences?.at(-1)?.result;
  if (activityStatus === 'complete' && !file?.isPlaceholder) {
    const earned = result?.earned_photo_approval;
    if (earned !== undefined) {
      if (earned?.file_id !== String(file?._id) || typeof earned.eligible !== 'boolean'
        || !Array.isArray(earned.matches) || !earned.matches.every(text)) return { ...unknown(), matches: [] };
      return { eligible: earned.eligible, status: 'earned', reason: null, matches: earned.eligible ? earned.matches : [] };
    }
    // Before per-image validation, recorded pose matches earned credit even
    // when the session's body-composition comparability check failed. Preserve
    // only those recorded matches, never infer all requirements from status.
    if (result && result.photo_validation?.version === undefined) {
      const raw = Array.isArray(result.matched_requirements) ? result.matched_requirements : [result.matched_requirement];
      const matches = raw.filter(text);
      if (matches.length) return { eligible: true, status: 'earned', reason: null, matches: [...new Set(matches)] };
    }
    // Processing/error state describes the rerun, not historical approval.
    file = { ...file, inferenceStatus: 'completed' };
  }
  if (file?.isPlaceholder || ['processing', 'started', 'failed'].includes(file?.inferenceStatus)) {
    return { ...unknown(), reason: 'Photo analysis is pending or failed. Analyze this photo before submitting.', matches: [] };
  }
  const eligibility = getPhotoEligibility(result, constraints, file?._id ?? null);
  const raw = Array.isArray(result?.matched_requirements) ? result.matched_requirements : [result?.matched_requirement];
  const matches = eligibility.eligible && raw.every(text) ? [...new Set(raw)] : [];
  return { ...eligibility, matches };
}

function arePhotoFilesEligible(files, constraints = []) {
  if (constraints.length > 0 && files.length === 0) return false;
  return files.every(file => {
    // Ordinary unanalysed attachments remain usable when no photo criteria
    // were configured. Once an analysis exists, its failures cannot be bypassed.
    if (constraints.length === 0 && !file.inferences?.length
      && !['processing', 'started', 'failed'].includes(file.inferenceStatus) && !file.isPlaceholder) return true;
    return getFilePhotoEvidence(file, constraints).eligible;
  });
}

module.exports = { getPhotoEligibility, getFilePhotoEvidence, arePhotoFilesEligible, resolvePhotoValidationContract };
